/**
 * Bot player logic for Chaos Chess.
 *
 * All functions are pure (no DOM, no side effects) and run in Node.js.
 * The random bot selects a uniformly random legal move each turn.
 */

import type { GameState, Move, GameMode, PieceType } from './types'
import { getLegalMoves, isPawnPromotion } from './rules'
import { getLegalMoves as getPseudoLegalMoves, applyMoveToState } from './moves'
import { buildInitialState } from './Board'
import { resolvePendingMoves } from './simultaneous'
import { processEventQueue } from './events'

// ── Core bot logic ─────────────────────────────────────────────────────────────

/**
 * Internal fast variant used only in simulateGame.
 * Uses pseudo-legal moves (no check filtering) — O(n) instead of O(n²).
 * Not exported — simulation only.
 */
function getBotMoveFast(state: GameState, playerIndex: number): Move | null {
  const player = state.players[playerIndex]
  if (!player || !player.alive) return null

  const candidates: Move[] = []
  const kingCaptures: Move[] = []
  for (const [fromKey, sq] of Object.entries(state.squares)) {
    if (!sq.piece || sq.piece.owner !== playerIndex) continue
    const dests = getPseudoLegalMoves(state, fromKey)
    for (const toKey of dests) {
      const move: Move = { player: playerIndex, from: fromKey, to: toKey }
      if (isPawnPromotion(state, move)) move.promotion = 'queen'
      candidates.push(move)
      if (state.squares[toKey]?.piece?.type === 'king') kingCaptures.push(move)
    }
  }

  // Always capture a king if possible — ensures simulation converges reliably
  if (kingCaptures.length > 0) return kingCaptures[Math.floor(Math.random() * kingCaptures.length)]
  if (candidates.length === 0) return null
  return candidates[Math.floor(Math.random() * candidates.length)]
}

/**
 * Select a random legal move for the given player.
 * Returns null if the player has no legal moves (stalemate, eliminated, etc.).
 * Pawns that promote always choose queen.
 * Uses full legal move generation including check filtering.
 */
export function getBotMove(state: GameState, playerIndex: number): Move | null {
  const player = state.players[playerIndex]
  if (!player || !player.alive) return null

  // Collect all legal moves across all pieces owned by this player
  const candidates: Move[] = []

  for (const [fromKey, sq] of Object.entries(state.squares)) {
    if (!sq.piece || sq.piece.owner !== playerIndex) continue
    const dests = getLegalMoves(state, fromKey)
    for (const toKey of dests) {
      const move: Move = { player: playerIndex, from: fromKey, to: toKey }
      // Auto-promote to queen
      if (isPawnPromotion(state, move)) {
        move.promotion = 'queen' as PieceType
      }
      candidates.push(move)
    }
  }

  if (candidates.length === 0) return null

  // Uniform random selection
  const idx = Math.floor(Math.random() * candidates.length)
  return candidates[idx]
}

// ── Lightweight simulation pipeline ───────────────────────────────────────────
//
// The full rules pipeline (evaluateEliminations → getCheckStatus → wouldLeaveInCheck
// → applyMoveToState) is O(pieces²) per turn and OOMs on a 4-player 14×14 game.
//
// For headless simulation we only need king-capture elimination (O(n) scan) and a
// simple win-condition check. Checkmate and stalemate detection are tested separately
// in rules.test.ts — they don't need exercising in every simulation turn.

/**
 * Scan the board for players whose king was captured and mark them eliminated.
 * Returns a new state; does not call getCheckStatus (expensive).
 */
function eliminateKinglesPlayers(state: GameState): GameState {
  const playersWithKing = new Set<number>()
  for (const sq of Object.values(state.squares)) {
    if (sq.piece?.type === 'king' && sq.piece.owner !== undefined) {
      playersWithKing.add(sq.piece.owner)
    }
  }

  const players = state.players.map(p =>
    p.alive && !playersWithKing.has(p.index) ? { ...p, alive: false } : p
  )
  return { ...state, players }
}

/**
 * Check win condition: gameover when ≤1 alive players remain.
 */
function checkWinSimple(state: GameState): GameState {
  const alive = state.players.filter(p => p.alive)
  if (alive.length <= 1) {
    return {
      ...state,
      phase: 'gameover',
      winner: alive.length === 1 ? alive[0].index : null,
    }
  }
  return state
}

/**
 * Apply a move and run the lightweight post-move pipeline for simulation.
 * Only checks king capture (not checkmate/stalemate).
 * Clears moveHistory to avoid O(N²) spreading over long simulations.
 */
function applyMoveSimulation(state: GameState, move: Move): GameState {
  // Clear history before applying to prevent O(N²) spreading in applyMoveToState
  const stateNoHistory: GameState = { ...state, moveHistory: [] }
  let next = applyMoveToState(stateNoHistory, move)
  // Fire board events if the turn number advanced
  if (next.eventQueue.length > 0 && next.turnNumber !== state.turnNumber) {
    const [evState] = processEventQueue(next)
    next = evState
  }
  return checkWinSimple(next)
}

// ── Simultaneous resolution helper (lightweight) ──────────────────────────────

/**
 * Resolve all pending moves and apply the lightweight post-round pipeline.
 * We clear moveHistory before resolving to avoid O(N²) spreading cost inside
 * resolvePendingMoves (which calls applyMoveToState 4× per round, each spreading
 * the accumulated history). Simulation doesn't need move history.
 */
function resolveSimultaneous(state: GameState): GameState {
  const stateNoHistory: GameState = { ...state, moveHistory: [] }
  let next = resolvePendingMoves(stateNoHistory)
  next = { ...next, moveHistory: [] }
  next = eliminateKinglesPlayers(next)
  // Fire board events — resolvePendingMoves always increments turnNumber
  if (next.eventQueue.length > 0) {
    const [evState] = processEventQueue(next)
    next = evState
  }
  next = checkWinSimple(next)
  return next
}

// ── Headless game simulation ───────────────────────────────────────────────────

export type SimulateConfig = {
  boardSize: 8 | 14
  playerCount: 2 | 3 | 4
  mode: GameMode
}

export type SimulateOptions = {
  maxTurns?: number
}

/**
 * Run a complete all-bot game synchronously without any timers or DOM.
 * Returns the final GameState (phase === 'gameover' or maxTurns reached).
 *
 * This is the primary headless testing tool — runs in Vitest with no browser.
 */
export function simulateGame(
  config: SimulateConfig,
  options: SimulateOptions = {}
): GameState {
  const maxTurns = options.maxTurns ?? 2000

  // Build state with all bot slots
  const botSlots = Array.from({ length: config.playerCount }, () => true)
  let state = buildInitialState({
    boardSize: config.boardSize,
    playerCount: config.playerCount,
    mode: config.mode,
    eliminationMode: 'last_standing',
    timerDuration: 0,
    eventsEnabled: true,
    startLayout: 'side',
    botSlots,
  })

  let turns = 0

  if (config.mode === 'sequential') {
    while (state.phase !== 'gameover' && turns < maxTurns) {
      const active = state.activePlayer
      const player = state.players[active]

      if (!player || !player.alive) {
        // Shouldn't happen — advanceSimple handles this — but guard to avoid infinite loop
        break
      }

      const move = getBotMoveFast(state, active)
      if (move === null) {
        // No pseudo-legal moves: mark player as stuck and advance
        // (applyMoveToState handles advancement, so we do it manually here)
        const players = state.players.map((p, i) => i === active ? { ...p, alive: false } : p)
        const nextActive = (active + 1) % players.length
        state = checkWinSimple({ ...state, players, activePlayer: nextActive, moveHistory: [] })
      } else {
        state = applyMoveSimulation(state, move)
      }

      turns++
    }
  } else if (config.mode === 'simultaneous_resolution') {
    while (state.phase !== 'gameover' && turns < maxTurns) {
      const alivePlayers = state.players.filter(p => p.alive)
      if (alivePlayers.length === 0) break

      // All alive bots submit pending moves (fast variant — no check filtering)
      let pending = { ...state, pendingMoves: [] as typeof state.pendingMoves }
      for (const p of alivePlayers) {
        const move = getBotMoveFast(state, p.index)
        if (move !== null) {
          pending = { ...pending, pendingMoves: [...pending.pendingMoves, move] }
        }
      }

      state = resolveSimultaneous(pending)
      turns++
    }
  } else {
    // simultaneous_instantaneous: same as resolution for headless purposes
    // (no timers — just batch submit and resolve each round)
    while (state.phase !== 'gameover' && turns < maxTurns) {
      const alivePlayers = state.players.filter(p => p.alive)
      if (alivePlayers.length === 0) break

      let pending = { ...state, pendingMoves: [] as typeof state.pendingMoves, windowOpenAt: 1 }
      for (const p of alivePlayers) {
        const move = getBotMoveFast(state, p.index)
        if (move !== null) {
          pending = { ...pending, pendingMoves: [...pending.pendingMoves, move] }
        }
      }

      state = resolveSimultaneous(pending)
      turns++
    }
  }

  return state
}
